import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    getActingTenantId,
    reconcileActingTenant,
    setActingTenantId,
    type TenantMembership,
} from './actingTenant';
import { AuthContext, type AuthStatus, type EditorAuthState } from './AuthContext';
import { authClient, type EmgAuthClaims } from './authClient';
import { authConfig, usesSso } from './authConfig';
import { fetchMeAccess } from './meAccess';

const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

/**
 * EAA-1694: owns the editor's SSO session state — identity claims, the tenants the
 * identity may act in, and which one is currently selected.
 *
 * In `token` mode this resolves straight to `anonymous` and does nothing else: the editor
 * keeps working off the hand-pasted API token exactly as before, and no OIDC network call
 * is made. That is why the provider can wrap the whole tree unconditionally.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [status, setStatus] = useState<AuthStatus>(usesSso ? 'loading' : 'anonymous');
    const [claims, setClaims] = useState<EmgAuthClaims | null>(null);
    const [memberships, setMemberships] = useState<TenantMembership[]>([]);
    const [actingTenantId, setActing] = useState<string | null>(getActingTenantId());

    useEffect(() => {
        if (!usesSso) {
            return;
        }
        let cancelled = false;

        void (async () => {
            try {
                const token = await authClient.getAccessToken();
                if (cancelled) return;
                if (!token) {
                    setStatus('anonymous');
                    return;
                }
                const [identity, access] = await Promise.all([
                    authClient.getClaims(),
                    fetchMeAccess(apiBaseUrl, token),
                ]);
                if (cancelled) return;
                setClaims(identity);
                setMemberships(access.memberships);
                setActing(reconcileActingTenant(access.memberships));
                // A verified identity with zero memberships is a real, distinct state, not
                // an error: authentication succeeded and access has simply not been
                // granted (or was revoked). It needs its own screen, because telling
                // someone to "sign in" when they already are is a dead end.
                setStatus(access.memberships.length > 0 ? 'authenticated' : 'no-access');
            } catch {
                if (!cancelled) setStatus('anonymous');
            }
        })();

        // Refresh failure, refresh-token reuse revoke, or back-channel single logout.
        const unsubscribe = authClient.onSessionEnded(() => {
            if (cancelled) return;
            setClaims(null);
            setMemberships([]);
            setStatus('anonymous');
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    const selectTenant = useCallback(
        (tenantId: string) => {
            // Never accept a tenant the identity has no membership in: it would be
            // asserted on every subsequent request and 403 with no explanation.
            if (!memberships.some((m) => m.tenantId === tenantId)) {
                return;
            }
            setActingTenantId(tenantId);
            setActing(tenantId);
        },
        [memberships]
    );

    const login = useCallback(() => {
        // EAA-1765: the portal is the single visible login entry for the suite.
        const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
        window.location.assign(
            `${authConfig.portalUrl}/login?returnTo=${encodeURIComponent(returnTo)}`
        );
    }, []);

    const logout = useCallback(() => {
        setActingTenantId(null);
        void authClient.logout();
    }, []);

    const permissions = useMemo(() => {
        const active = memberships.find((m) => m.tenantId === actingTenantId);
        return new Set<string>(active?.permissions ?? []);
    }, [memberships, actingTenantId]);

    const value = useMemo<EditorAuthState>(
        () => ({
            status,
            claims,
            memberships,
            actingTenantId,
            permissions,
            can: (code: string) => permissions.has(code),
            selectTenant,
            login,
            logout,
        }),
        [status, claims, memberships, actingTenantId, permissions, selectTenant, login, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
